import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ExecutiveTeam from "@/components/ExecutiveTeam";
import VideoPlayer from "@/components/VideoPlayer";

const FOUNDER_IMG = "/founder-sara.jpg";
const JEANINE_IMAGES = ["/jeanine-2.webp"];
const FACTORY_IMG = "/faytex-factory.webp";
const STAFF_IMG   = "/exec-group.webp";

export default function Legacy() {
  const [jeanineIdx, setJeanineIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(
      () => setJeanineIdx((i) => (i + 1) % JEANINE_IMAGES.length),
      5000
    );
    return () => clearInterval(id);
  }, []);
  return (
    <div data-testid="legacy-page" className="bg-white">
      <section className="relative isolate overflow-hidden bg-faytex-ink text-white">
        <div className="absolute inset-0 -z-10 opacity-40">
          <img src={FACTORY_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-40">
          <p className="text-xs uppercase tracking-widest text-faytex-pink">The Faytex Legacy</p>
          <h1 className="font-heading text-4xl sm:text-5xl md:text-7xl mt-3 tracking-tighter max-w-4xl">
            A Mother. A Daughter. A Legacy.
          </h1>
          <p className="mt-6 text-white/85 max-w-3xl text-lg leading-relaxed">
            For over four decades, Fay International has been building more than feminine hygiene
            products — we&rsquo;ve been building confidence.
          </p>
          <p className="mt-4 text-white/80 max-w-3xl text-lg leading-relaxed">
            Founded by Sara Galloway and now led by her daughter, Jeanine Hage-Ali, Fay
            International has grown from a family dream into one of Ghana&rsquo;s longest-established
            manufacturers of feminine hygiene products.
          </p>
          <p className="mt-4 text-white/80 max-w-3xl text-lg leading-relaxed">
            Built on resilience, innovation and an unwavering commitment to women, our story
            continues to evolve — one generation inspiring the next.
          </p>
        </div>
      </section>

      {/* Founder Story */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 relative">
          <img src={FOUNDER_IMG} alt="Founder Sara Galloway" className="rounded-3xl shadow-hover w-full h-[500px] object-cover" />
          <div className="absolute -bottom-6 -left-6 hidden md:block bg-faytex-pink text-white rounded-2xl p-5 max-w-[220px] shadow-hover">
            <p className="font-heading text-2xl">1980</p>
            <p className="text-xs mt-1 opacity-90">Sara founds SAPAD Manufacturing Company in Ghana.</p>
          </div>
        </div>
        <div className="lg:col-span-7">
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">The Founder</p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">Sara Galloway</h2>
          <p className="mt-5 text-faytex-muted leading-relaxed text-lg">
            In a time when sanitary pads were imported, expensive, and rarely built for Ghanaian
            women, Sara saw a gap — and a calling. In 1980 she founded
            {" "}<strong className="text-faytex-ink">SAPAD Manufacturing Company</strong>, starting
            with a small team and an even smaller workshop, determined to make a pad that women
            could trust, afford, and feel proud to carry.
          </p>
          <p className="mt-4 text-faytex-muted leading-relaxed">
            In <strong className="text-faytex-ink">1994</strong>, SAPAD was renamed
            {" "}<strong className="text-faytex-ink">Fay International Limited</strong> — the name it
            still carries today as the home of Faytex.
          </p>
          <blockquote className="mt-8 border-l-4 border-faytex-pink pl-6 font-heading text-2xl md:text-3xl text-faytex-ink leading-snug">
            &ldquo;I wanted every girl in Ghana to feel taken care of. That is the only mission.&rdquo;
          </blockquote>
          <p className="mt-4 text-sm text-faytex-muted">— Sara Galloway, Founder</p>
        </div>
      </section>

      {/* The Chairman — Joseph Hage-Ali */}
      <section className="bg-faytex-pinkLight/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 order-1">
            <div className="relative rounded-3xl overflow-hidden shadow-hover bg-faytex-ink">
              <img
                src="/joseph-hage-ali.jpg"
                alt="Joseph Hage-Ali, Chairman of Fay International Limited"
                className="w-full h-[500px] object-cover"
                style={{ objectPosition: "center 15%" }}
              />
              <span className="absolute top-5 left-5 rounded-full bg-faytex-pink text-white text-xs uppercase tracking-widest font-semibold px-4 py-1.5">
                Chairman
              </span>
            </div>
          </div>
          <div className="lg:col-span-7 order-2">
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">The Chairman</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">Joseph Hage-Ali</h2>
            <p className="mt-2 text-faytex-muted text-sm uppercase tracking-widest">Chairman, Fay International Limited</p>

            <p className="mt-6 text-faytex-muted leading-relaxed text-lg">
              Joseph Hage-Ali is a business leader and entrepreneur whose life and career have been
              shaped by family, service, hard work and a deep commitment to people.
            </p>
            <p className="mt-4 text-faytex-muted leading-relaxed">
              Born in Dakar, Senegal, to Lebanese-French parents, Joseph spent two years serving in
              the French Army in Germany during his early adulthood. The experience reinforced
              values of discipline, resilience and responsibility that would later influence both
              his professional and personal life.
            </p>
            <p className="mt-4 text-faytex-muted leading-relaxed">
              After returning to Ghana, he entered the business world, working with various trading
              companies before becoming closely involved with Fay International Limited. He later
              became Chairman of the company, a position he continues to hold today.
            </p>
            <p className="mt-4 text-faytex-muted leading-relaxed">
              Throughout his career, Joseph has remained guided by a strong belief in honesty, hard
              work and respect for others. Beyond his professional achievements, however, he
              considers family one of the most important parts of his life. Meeting his wife,
              building a life and family together, and eventually seeing their children become
              involved in the family business are among the milestones he values most.
            </p>
            <p className="mt-4 text-faytex-muted leading-relaxed">
              Following the loss of his wife, her memory and the life they built together have
              remained an important part of the family&rsquo;s story and the legacy of Fay
              International.
            </p>
            <p className="mt-4 text-faytex-muted leading-relaxed">
              Today, Joseph continues to provide leadership, experience and guidance to the company
              while supporting the next generation as it takes Fay International forward. For him,
              the company represents more than a business; it is part of a family legacy built over
              decades, with a responsibility to preserve its values while creating something
              stronger for the generations that follow.
            </p>
          </div>
        </div>
      </section>

      {/* The Next Generation — Jeanine */}
      <section className="bg-faytex-pinkLight/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">The Next Generation</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">Jeanine Hage-Ali</h2>
            <p className="mt-2 text-faytex-muted text-sm uppercase tracking-widest">Managing Director, Fay International Limited</p>
            <p className="mt-6 text-faytex-muted leading-relaxed text-lg">
              Following in the footsteps of founder Sara Galloway, Jeanine Hage-Ali is leading
              Fay International into its next chapter through investment in modern manufacturing,
              operational excellence and sustainable growth.
            </p>
            <p className="mt-4 text-faytex-muted leading-relaxed text-lg">
              Since assuming leadership, she has overseen the company&rsquo;s expansion, including the
              establishment of its modern manufacturing facility in Kpone, Ghana, while strengthening
              Fay International&rsquo;s position as one of Ghana&rsquo;s leading manufacturers of
              feminine hygiene products.
            </p>
            <p className="mt-4 text-faytex-muted leading-relaxed text-lg">
              Building on the legacy established by her mother, Jeanine continues to champion African
              manufacturing, innovation and the empowerment of women through products that improve
              everyday confidence and wellbeing.
            </p>
            <p className="mt-4 text-faytex-muted leading-relaxed text-lg">
              Over the years, Fay International has proudly supported initiatives that celebrate
              women&rsquo;s strength and achievement, including previous partnerships with Ghana&rsquo;s
              Black Queens and Bukom Boxing Arena. Today, the company continues that legacy through
              manufacturing excellence, community engagement and menstrual health education.
            </p>
            <blockquote className="mt-8 border-l-4 border-faytex-pink pl-6 font-heading text-2xl md:text-3xl text-faytex-ink leading-snug">
              &ldquo;My mother built this for one girl. I&rsquo;m building it for every girl on the continent.&rdquo;
            </blockquote>
            <p className="mt-4 text-sm text-faytex-muted">— Jeanine Hage-Ali, Managing Director</p>
          </div>
          <div className="lg:col-span-5 relative order-1 lg:order-2">
            <div
              data-testid="jeanine-slider"
              className="relative rounded-3xl overflow-hidden shadow-hover w-full h-[500px] bg-faytex-pinkLight"
            >
              {JEANINE_IMAGES.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt="Jeanine Hage-Ali, Managing Director of Fay International"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                    i === jeanineIdx ? "opacity-100" : "opacity-0"
                  }`}
                  style={{ objectPosition: "center 30%" }}
                />
              ))}
              {/* Dots hide automatically when only one image */}
              {JEANINE_IMAGES.length > 1 && (
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 z-10">
                  {JEANINE_IMAGES.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`jeanine-slide-dot-${i}`}
                      aria-label={`Show image ${i + 1}`}
                      onClick={() => setJeanineIdx(i)}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        i === jeanineIdx ? "bg-white w-6" : "bg-white/50 hover:bg-white/80"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:block bg-faytex-ink text-white rounded-2xl p-5 max-w-[220px] shadow-hover">
              <p className="font-heading text-2xl text-faytex-pink">Today</p>
              <p className="text-xs mt-1 opacity-90">Jeanine leads Faytex forward.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Executive Team */}
      <ExecutiveTeam />

      {/* Rebirth Story */}
      <section className="bg-faytex-pinkLight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">The Rebirth</p>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">2021. The fire. And the comeback.</h2>
            <p className="mt-5 text-faytex-muted leading-relaxed text-lg">
              In 2021, a devastating fire destroyed our original production facility.
            </p>
            <p className="mt-4 font-heading text-2xl md:text-3xl text-faytex-ink leading-snug">
              Buildings can be rebuilt, purpose cannot be destroyed.
            </p>
            <p className="mt-4 text-faytex-muted leading-relaxed">
              Driven by the resilience of our people and our commitment to the women who depend on
              Faytex, we rebuilt stronger than ever.
            </p>
            <p className="mt-4 text-faytex-muted leading-relaxed">
              Today, our modern manufacturing facility in Kpone stands as a symbol of perseverance,
              innovation and the belief that every challenge is an opportunity to build something
              better.
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4">
              {[
                { k: "0", v: "Days we stopped" },
                { k: "3x",  v: "Capacity increase" },
                { k: "200+", v: "Jobs reinstated" },
              ].map((s) => (
                <div key={s.k} className="bg-white rounded-2xl p-5 text-center shadow-soft">
                  <p className="font-heading text-3xl text-faytex-pink">{s.k}</p>
                  <p className="text-[11px] text-faytex-muted uppercase tracking-widest mt-1">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="relative aspect-[3/2] rounded-3xl overflow-hidden shadow-hover bg-[#1f5aa8]">
            <img src={STAFF_IMG} alt="Faytex executive team" className="w-full h-full object-cover md:object-contain" />
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none" />
            <div className="absolute bottom-0 left-0 p-6 text-white">
              <p className="font-heading text-2xl">The Fire and The Comeback</p>
              <p className="text-sm opacity-90 mt-1">Hundreds of Ghanaian women &amp; men make every pack.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cinematic Video */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">The Faytex Brand Film</p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mt-3 tracking-tight">Inside our Kpone Factory.</h2>
          <p className="mt-4 text-faytex-muted">Cinematic drone shots, the machinery, and the women who run it.</p>
        </div>
        <div className="mt-12 relative aspect-video rounded-3xl overflow-hidden shadow-hover bg-faytex-ink">
          <VideoPlayer
            testid="legacy-video"
            src="/factory-video.mp4"
            poster="/factory-poster.jpg"
            ariaLabel="Play factory film"
            className="w-full h-full"
          />
          <div className="pointer-events-none absolute bottom-0 left-0 p-8 text-white bg-gradient-to-t from-black/50 via-black/20 to-transparent w-full">
            <p className="font-heading text-2xl md:text-3xl max-w-xl">The Pride of Ghana — Inside the Kpone Factory</p>
          </div>
        </div>
      </section>

      <section className="bg-faytex-ink text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-24 text-center">
          <p className="text-xs uppercase tracking-widest text-faytex-pink font-semibold">Made in Ghana</p>
          <h2 className="font-heading text-3xl md:text-5xl tracking-tight mt-3">The DNA of a Faytex Pad</h2>
          <p className="mt-6 text-white/85 leading-relaxed text-lg">
            Every Faytex pad is thoughtfully designed to deliver the comfort, protection and
            confidence women deserve.
          </p>
          <p className="mt-4 text-white/80 leading-relaxed">
            From soft, skin-friendly materials to reliable absorbency and secure protection, every
            layer is carefully engineered to support women through every stage of their day.
          </p>
          <p className="mt-4 text-white/80 leading-relaxed">
            Proudly manufactured in Ghana, every Faytex product reflects decades of manufacturing
            expertise, continuous innovation and an uncompromising commitment to quality.
          </p>
          <p className="mt-6 font-heading text-2xl md:text-3xl text-faytex-pink leading-snug">
            Because confidence starts with protection you can trust.
          </p>
          <Link
            to="/shop"
            data-testid="legacy-b2b-cta"
            className="inline-flex items-center gap-2 mt-10 rounded-full bg-faytex-pink hover:bg-faytex-pinkHover px-7 py-3.5 text-sm font-semibold transition-colors"
          >
            Shop Faytex Products
          </Link>
        </div>
      </section>
    </div>
  );
}
